import { useEffect, useMemo, useState } from 'react'

import { Button } from '../../components/ui/Button'
import { DataTable, type Column, type SortState } from '../../components/ui/DataTable'
import { EmptyState } from '../../components/ui/EmptyState'
import { Money } from '../../components/ui/Money'
import { StatusBadge, employeeStatusTone } from '../../components/ui/StatusBadge'
import { ApiError, listEmployees, type Employee } from '../../lib/api'

/** Maps the table's sort state onto the API's sort parameter. */
function sortParam(sort: SortState): string {
  const prefix = sort.direction === 'descending' ? '-' : ''
  return `${prefix}${sort.key}`
}

export function EmployeesPage({ permissions }: { permissions: Record<string, string> }) {
  const [rows, setRows] = useState<Employee[]>([])
  const [sort, setSort] = useState<SortState>({ key: 'name', direction: 'ascending' })
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string[]>([])
  const [cursor, setCursor] = useState<string | undefined>()
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [showSkeleton, setShowSkeleton] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canSeeCompensation = 'hr:compensation:read' in permissions
  const canCreate = 'hr:employee:write' in permissions
  const isFiltered = search !== '' || statusFilter.length > 0

  // Debounced so typing a name does not fire a request per keystroke, and
  // cancelled on change so a slow earlier response cannot overwrite a newer
  // one — the out-of-order race that makes search results flicker back to a
  // previous query.
  useEffect(() => {
    let cancelled = false
    const timer = setTimeout(async () => {
      setLoading(true)
      setError(null)
      try {
        const page = await listEmployees({
          sort: sortParam(sort),
          q: search || undefined,
          status: statusFilter.length ? statusFilter : undefined,
          limit: 50,
        })
        if (cancelled) return
        setRows(page.items)
        setCursor(page.next_cursor)
        setHasMore(page.has_more)
      } catch (err) {
        if (cancelled) return
        setError(err instanceof ApiError ? err.problem.detail : 'Could not load employees.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, search ? 250 : 0)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [sort, search, statusFilter])

  // Skeleton rows appear only if loading actually takes a noticeable moment.
  // A shimmer on every fast response makes the app feel slower than it is.
  useEffect(() => {
    if (!loading) {
      setShowSkeleton(false)
      return
    }
    const timer = setTimeout(() => setShowSkeleton(true), 400)
    return () => clearTimeout(timer)
  }, [loading])

  async function loadMore() {
    if (!cursor) return
    setLoading(true)
    try {
      const page = await listEmployees({ cursor, sort: sortParam(sort), limit: 50 })
      setRows((prev) => [...prev, ...page.items])
      setCursor(page.next_cursor)
      setHasMore(page.has_more)
    } catch (err) {
      setError(err instanceof ApiError ? err.problem.detail : 'Could not load more results.')
    } finally {
      setLoading(false)
    }
  }

  const columns = useMemo<Column<Employee>[]>(() => {
    const base: Column<Employee>[] = [
      {
        key: 'employee_number',
        header: 'No.',
        sortable: true,
        width: '7rem',
        render: (e) => <span className="lg-numeric">{e.employee_number}</span>,
      },
      {
        key: 'name',
        header: 'Name',
        sortable: true,
        render: (e) => (
          <div>
            <div>{e.full_name}</div>
            <div className="lg-subtle" style={{ fontSize: 'var(--lg-text-xs)' }}>
              {e.work_email}
            </div>
          </div>
        ),
      },
      {
        key: 'employment_type',
        header: 'Type',
        width: '9rem',
        render: (e) => <span className="lg-muted">{e.employment_type.replace('_', ' ')}</span>,
      },
      {
        key: 'status',
        header: 'Status',
        width: '9rem',
        render: (e) => {
          const meta = employeeStatusTone[e.status] ?? { tone: 'neutral' as const, label: e.status }
          return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>
        },
      },
      {
        key: 'hire_date',
        header: 'Hired',
        sortable: true,
        align: 'end',
        width: '8rem',
        render: (e) => e.hire_date,
      },
    ]

    // The column is added only when the permission is held. The API also
    // omits the field entirely in that case, so this is presentation
    // consistency rather than the access control itself.
    if (canSeeCompensation) {
      base.splice(4, 0, {
        key: 'compensation',
        header: 'Base salary',
        align: 'end',
        width: '11rem',
        render: (e) =>
          e.compensation ? (
            <Money
              amount={e.compensation.amount}
              currency={e.compensation.currency}
              showCurrency={false}
            />
          ) : (
            <span className="lg-subtle">—</span>
          ),
      })
    }
    return base
  }, [canSeeCompensation])

  return (
    <>
      <header className="lg-page-header">
        <div>
          <h1>Employees</h1>
          <p className="lg-muted" style={{ fontSize: 'var(--lg-text-sm)' }}>
            Everyone on the books, current and former.
          </p>
        </div>
        {canCreate && (
          <div className="lg-page-header__actions">
            <Button variant="primary">Add employee</Button>
          </div>
        )}
      </header>

      <div className="lg-toolbar">
        <input
          className="lg-input"
          type="search"
          placeholder="Search name, number, or email"
          aria-label="Search employees"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ minWidth: '18rem' }}
        />
        <select
          className="lg-input"
          aria-label="Filter by status"
          value={statusFilter[0] ?? ''}
          onChange={(e) => setStatusFilter(e.target.value ? [e.target.value] : [])}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="onboarding">Onboarding</option>
          <option value="on_leave">On leave</option>
          <option value="suspended">Suspended</option>
          <option value="terminated">Terminated</option>
        </select>
        {isFiltered && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearch('')
              setStatusFilter([])
            }}
          >
            Clear filters
          </Button>
        )}
      </div>

      {error && (
        <div className="lg-alert" role="alert" style={{ marginBottom: 'var(--lg-space-4)' }}>
          {error}
        </div>
      )}

      <DataTable
        caption="Employees, with number, name, employment type, status, and hire date"
        columns={columns}
        rows={rows}
        getRowId={(e) => e.id}
        sort={sort}
        onSortChange={setSort}
        onRowActivate={(e) => {
          window.location.href = `/hr/employees/${e.id}`
        }}
        loading={showSkeleton}
        emptyState={
          // "Nothing yet" and "nothing matched" are genuinely different
          // situations and get different offers. Conflating them means telling
          // someone to create a record that already exists behind a filter.
          isFiltered ? (
            <EmptyState
              title="No matches"
              body="No employee matches these filters. Try a broader search or clear the filters."
              action={
                <Button
                  onClick={() => {
                    setSearch('')
                    setStatusFilter([])
                  }}
                >
                  Clear filters
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="No employees yet"
              body="Add your first employee, or import your existing team from a spreadsheet."
              action={canCreate ? <Button variant="primary">Add employee</Button> : undefined}
            />
          )
        }
        footer={
          <>
            <span>
              {rows.length} shown
              {hasMore ? ' · more available' : ''}
            </span>
            {hasMore && (
              <Button size="sm" onClick={loadMore} loading={loading}>
                Load more
              </Button>
            )}
          </>
        }
      />
    </>
  )
}
